import {useState} from 'react';
import {Platform, StyleSheet, Text, View, TouchableOpacity, ActivityIndicator} from "react-native";
import Alert from "@/lib/Alert";
import {Info, ExternalLink} from "lucide-react-native";
import {ThemeType} from "@/theming/baseTheme";
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {AppDataOrganization} from "@/data/app-data-interfaces";
import {useTranslation} from "react-i18next";
import {createWebToken} from "@/data/app-data-api";
import {appConfig} from "@/app-config";

interface Props {
    organization: AppDataOrganization
}

export function MembershipDashboardLink({organization}: Props) {
    const {theme} = useCurrentTheme();
    const styles = createStyles(theme);
    const {t} = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const handleOpenDashboard = async () => {
        setIsLoading(true);
        try {
            // Create a web token
            const webTokenId = await createWebToken();

            // Get dashboard URL from environment
            const url = `${appConfig.dashboardUrl}/token/${webTokenId}`;

            // Open in new window (web only)
            window.open(url, '_blank');
        } catch (error) {
            console.error('Failed to open dashboard:', error);
            Alert.alert(
                t('common.error', {defaultValue: 'Error'}),
                t('membership.dashboard.openError', {
                    defaultValue: 'Failed to open dashboard. Please try again.',
                })
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* Management Info - Mobile only */}
            {Platform.OS !== 'web' && <View style={styles.infoCard}>
                <View style={styles.infoIcon}>
                    <Info size={20} color={theme.colors.primary} />
                </View>
                <View style={styles.infoContent}>
                    <Text style={styles.infoTitle}>{t('membership.management.title')}</Text>
                    <Text style={styles.infoText}>
                        {t('membership.management.description', { organizationName: organization.name })}
                    </Text>
                </View>
            </View>}

            {/* Dashboard Link Card - Web only */}
            {Platform.OS === 'web' && (
                <View style={styles.dashboardCard}>
                    <TouchableOpacity
                        style={[styles.dashboardButton, isLoading && styles.dashboardButtonDisabled]}
                        onPress={handleOpenDashboard}
                        disabled={isLoading}
                        activeOpacity={0.7}
                    >
                        <View style={styles.dashboardButtonContent}>
                            {isLoading ? (
                                <ActivityIndicator color={theme.colors.primary} style={styles.dashboardIcon} />
                            ) : (
                                <ExternalLink size={20} color={theme.colors.primary} style={styles.dashboardIcon} />
                            )}
                            <View style={styles.dashboardTextContainer}>
                                <Text style={styles.dashboardTitle}>
                                    {t('membership.dashboard.title', {defaultValue: 'Open Dashboard'})}
                                </Text>
                                <Text style={styles.dashboardSubtext}>
                                    {t('membership.dashboard.subtitle', {
                                        defaultValue: 'Manage your subscription and settings',
                                    })}
                                </Text>
                            </View>
                        </View>
                    </TouchableOpacity>
                </View>
            )}
        </>
    );
}

const createStyles = (theme: ThemeType) =>
    StyleSheet.create({
        container: {
            flex: 1,
            backgroundColor: theme.colors.background,
        },
        scrollView: {
            flex: 1,
        },
        loadingContainer: {
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
        },
        loadingText: {
            color: theme.colors.text,
            fontSize: theme.fontSize.md,
        },
        planCard: {
            margin: theme.spacing.md,
            backgroundColor: theme.colors.primary,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.lg,
            shadowColor: theme.colors.primary,
            shadowOffset: {
                width: 0,
                height: 4,
            },
            shadowOpacity: 0.2,
            shadowRadius: 8,
            elevation: 5,
        },
        planHeader: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: theme.spacing.md,
        },
        planName: {
            fontSize: theme.fontSize.xl,
            fontWeight: '700',
            color: '#FFFFFF',
        },
        statusBadge: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: theme.spacing.xs,
            paddingHorizontal: theme.spacing.sm,
            borderRadius: theme.borderRadius.full,
            gap: theme.spacing.xs,
        },
        statusActive: {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
        },
        statusInactive: {
            backgroundColor: theme.colors.error,
        },
        statusText: {
            color: '#FFFFFF',
            fontSize: theme.fontSize.sm,
            fontWeight: '600',
        },
        priceContainer: {
            alignItems: 'center',
        },
        priceAmount: {
            fontSize: 36,
            fontWeight: '700',
            color: '#FFFFFF',
        },
        pricePeriod: {
            fontSize: theme.fontSize.md,
            color: 'rgba(255, 255, 255, 0.8)',
        },
        detailsCard: {
            margin: theme.spacing.md,
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.lg,
            borderWidth: 1,
            borderColor: theme.colors.border,
        },
        sectionTitle: {
            fontSize: theme.fontSize.lg,
            fontWeight: '700',
            color: theme.colors.text,
            marginBottom: theme.spacing.lg,
        },
        detailItem: {
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: theme.spacing.lg,
        },
        detailIcon: {
            width: 40,
            height: 40,
            borderRadius: theme.borderRadius.md,
            backgroundColor: theme.colors.background,
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: theme.spacing.md,
        },
        detailContent: {
            flex: 1,
        },
        detailLabel: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.textSecondary,
            marginBottom: 2,
        },
        detailValue: {
            fontSize: theme.fontSize.md,
            fontWeight: '600',
            color: theme.colors.text,
        },
        detailValueError: {
            color: theme.colors.error,
        },
        orgCard: {
            margin: theme.spacing.md,
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.lg,
            borderWidth: 1,
            borderColor: theme.colors.border,
        },
        orgName: {
            fontSize: theme.fontSize.md,
            fontWeight: '600',
            color: theme.colors.text,
            marginBottom: theme.spacing.xs,
        },
        orgDescription: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.textSecondary,
        },
        infoCard: {
            margin: theme.spacing.md,
            backgroundColor: `${theme.colors.primary}10`,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.lg,
            flexDirection: 'row',
            borderWidth: 1,
            borderColor: `${theme.colors.primary}20`,
        },
        infoIcon: {
            marginRight: theme.spacing.md,
            marginTop: 2,
        },
        infoContent: {
            flex: 1,
        },
        infoTitle: {
            fontSize: theme.fontSize.md,
            fontWeight: '600',
            color: theme.colors.text,
            marginBottom: theme.spacing.xs,
        },
        infoText: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.textSecondary,
            lineHeight: theme.fontSize.sm * 1.5,
        },
        logoutSection: {
            marginTop: theme.spacing.lg,
            marginHorizontal: theme.spacing.md,
            paddingTop: theme.spacing.lg,
            borderTopWidth: 1,
            borderTopColor: theme.colors.border,
        },
        bottomSpacing: {
            height: theme.spacing.xl,
        },
        dashboardCard: {
            margin: theme.spacing.md,
        },
        dashboardButton: {
            backgroundColor: theme.colors.surface,
            padding: theme.spacing.lg,
            borderRadius: theme.borderRadius.lg,
            borderWidth: 1,
            borderColor: theme.colors.border,
            shadowColor: '#000',
            shadowOffset: {width: 0, height: 2},
            shadowOpacity: 0.05,
            shadowRadius: 4,
            elevation: 2,
        },
        dashboardButtonDisabled: {
            opacity: 0.6,
        },
        dashboardButtonContent: {
            flexDirection: 'row',
            alignItems: 'center',
        },
        dashboardIcon: {
            marginRight: theme.spacing.md,
        },
        dashboardTextContainer: {
            flex: 1,
        },
        dashboardTitle: {
            color: theme.colors.text,
            fontSize: theme.fontSize.md,
            fontWeight: '600',
            marginBottom: theme.spacing.xs,
        },
        dashboardSubtext: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.textSecondary,
            lineHeight: theme.fontSize.sm * 1.4,
        },
    });

